/**
 * Context quản lý danh sách yêu thích BĐS của user đã đăng nhập.
 *
 * Luồng dữ liệu:
 * - Phụ thuộc AuthContext: chỉ fetch favorites khi isAuthenticated.
 * - favoriteIds lưu dạng Set<string> để tra cứu O(1).
 * - toggleFavorite gọi API add/remove và cập nhật Set cục bộ optimistically.
 * - Export useFavorites hook cho FavoriteButton, FavoritesPage, v.v.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { favoriteApi } from '../features/favorites/api/favoriteApi';
import { useAuth } from '../hooks/useAuth';

const FavoriteContext = createContext(null);

export const FavoriteProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();

  /** Tập ID tin đã yêu thích — key là string property_id */
  const [favoriteIds, setFavoriteIds] = useState(new Set());

  /** Tải danh sách yêu thích từ API; reset rỗng khi chưa đăng nhập */
  const loadFavorites = useCallback(() => {
    if (!isAuthenticated) {
      setFavoriteIds(new Set());
      return Promise.resolve();
    }

    return favoriteApi
      .getFavorites()
      .then((response) => {
        const ids = new Set(
          (response.data.data || []).map((item) => String(item.property_id))
        );
        setFavoriteIds(ids);
      })
      .catch(() => setFavoriteIds(new Set()));
  }, [isAuthenticated]);

  /** Fetch lại khi trạng thái đăng nhập thay đổi */
  useEffect(() => {
    loadFavorites();
  }, [loadFavorites]);

  /** Kiểm tra một tin có trong danh sách yêu thích không */
  const isFavorite = useCallback(
    (propertyId) => favoriteIds.has(String(propertyId)),
    [favoriteIds]
  );

  /**
   * Bật/tắt yêu thích — trả về true nếu đã thêm, false nếu đã xóa.
   * Cập nhật Set cục bộ sau khi API thành công.
   */
  const toggleFavorite = useCallback(
    async (propertyId) => {
      const id = String(propertyId);

      if (favoriteIds.has(id)) {
        await favoriteApi.removeFavorite(id);
        setFavoriteIds((current) => {
          const next = new Set(current);
          next.delete(id);
          return next;
        });
        return false;
      }

      await favoriteApi.addFavorite(id);
      setFavoriteIds((current) => new Set(current).add(id));
      return true;
    },
    [favoriteIds]
  );

  const value = useMemo(
    () => ({
      favoriteIds,
      isFavorite,
      toggleFavorite,
      reloadFavorites: loadFavorites,
    }),
    [favoriteIds, isFavorite, loadFavorites, toggleFavorite]
  );

  return <FavoriteContext.Provider value={value}>{children}</FavoriteContext.Provider>;
};

/** Hook truy cập FavoriteContext — bắt buộc bọc trong FavoriteProvider */
export const useFavorites = () => {
  const context = useContext(FavoriteContext);

  if (!context) {
    throw new Error('useFavorites must be used within FavoriteProvider');
  }

  return context;
};
